import { useEffect } from 'react';
import { StoreApi } from 'zustand';

import {
  FilterGroupKeys,
  FilterGroups,
  FilterState,
} from '~/hooks/use-filter-actions/useFilterActions';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';

import { StoreWithFilterGroups } from './types';
import { compressFilters, decompressFilters } from './utils';

export const useFiltersQueryParam = <
  T extends FilterGroupKeys = FilterGroupKeys
>(
  store: StoreApi<StoreWithFilterGroups<T>>
) => {
  const { params, setQueryParams } = useQueryParams();

  const filterGroups = store.getState().filterGroups;
  const hasInitializedFilters =
    (Object.values(filterGroups) as FilterState[]).flatMap(
      group => group.allFilters
    ).length > 0;

  // Restore filters from URL on initial load
  useEffect(() => {
    const filterQueryKeyValue = params[queryKeys.filters];

    if (hasInitializedFilters && filterQueryKeyValue) {
      const currentFilterGroups = store.getState().filterGroups;
      const decompressedFilters = decompressFilters(
        filterQueryKeyValue,
        currentFilterGroups
      );

      if (decompressedFilters) {
        const updatedGroups = { ...currentFilterGroups } as FilterGroups<T>;
        Object.keys(decompressedFilters).forEach(groupKey => {
          const key = groupKey as T;

          if (updatedGroups[key]) {
            updatedGroups[key] = {
              ...updatedGroups[key],
              activeFilterOptions: decompressedFilters[key] || [],
            };
          }
        });

        store.setState({ filterGroups: updatedGroups });
      }
    }
  }, [hasInitializedFilters]);

  // Sync store changes to query params
  useEffect(() => {
    if (hasInitializedFilters) {
      const compressedFilters = compressFilters(filterGroups);
      const filterQueryKeyValue = compressedFilters
        ? compressedFilters
        : undefined;
      setQueryParams(
        { [queryKeys.filters]: filterQueryKeyValue },
        { replace: true }
      );
    }
  }, [hasInitializedFilters, setQueryParams, filterGroups]);
};
